<script setup lang="ts">
import { vOnClickOutside } from "@vueuse/components";
import type { ISelectDropdown } from "./ISelectDropdown";

const props = defineProps<ISelectDropdown>();
const emit = defineEmits<{
  (e: "itemSelected", itemValue: string): void;
}>();

const dropdownHidden = ref<boolean>(true);

const activeItemLabel = computed((): string => {
  const activeItemIndex = props.items.findIndex(
    (i) => i.value === props.activeItem
  );

  if (activeItemIndex < 0) return props.items[0].label;
  return props.items[activeItemIndex].label;
});

const closeDropdown = () => {
  dropdownHidden.value = true;
};

const toggleDropDown = () => {
  dropdownHidden.value = dropdownHidden.value ? false : true;
};

const itemSelected = (e: Event, itemValue: string) => {
  e.preventDefault();
  e.stopImmediatePropagation();
  emit("itemSelected", itemValue);
  closeDropdown();
};
</script>
<template>
  <section
    v-on-click-outside="closeDropdown"
    class="SelectDropdown"
    @click="toggleDropDown">
    <span>
      {{ activeItemLabel }}
    </span>
    <ul v-show="!dropdownHidden" class="dropdown-menu" role="menu">
      <li
        v-for="(item, index) of items"
        :key="index"
        @click="(e) => itemSelected(e, item.value)">
        {{ item.label }}
      </li>
    </ul>
  </section>
</template>
<style lang="scss">
.SelectDropdown {
  position: relative;
  color: $white;
  cursor: pointer;
  font-family: "Open Sans", sans-serif;
  @include f-14-500;
  background: $grey80;
  padding: 4px 36px 4px 16px;
  border: 1px solid $grey80;
  border-radius: 96px;
  width: auto;
  -webkit-tap-highlight-color: transparent;

  @media #{$break-xs} {
    width: auto;
  }

  span {
    &::after {
      font-family: $fontIcon;
      content: "\e90c";
      font-size: 12px;
      line-height: 12px;
      position: absolute;
      right: 16px;
      top: 8px;
      transition: transform 300ms cubic-bezier(0.37, 0.74, 0.15, 1);
    }
  }

  ul {
    font-family: "MuseoSans", Helvetica, Arial, sans-serif;
    position: absolute;
    background-color: $white;
    color: $grey70;
    min-width: 160px;
    border: 1px solid $grey60;
    box-shadow: $elevation-center-40;
    border-radius: 8px;
    margin: 9px 0;
    right: 0;
    overflow: hidden;

    li {
      @include f-14-500;
      padding: 12px 24px;

      &:hover {
        background: $grey10;
        color: $black;
      }
    }
  }
}
</style>
